export const getFileFields = form => {
  const fileFields = [];
  const seenFields = new Set();
  if (!form) return fileFields;

  const isFileType = type => ['FILE', 'IMAGE', 'MULTI-FILE', 'AUDIO', 'VIDEO'].includes(type?.toUpperCase());

  const traverse = secs => {
    secs?.forEach(section => {
      (section.fields || []).forEach(field => {
        if (isFileType(field.type)) {
          if (field.id && !seenFields.has(field.id)) {
            fileFields.push(field.id);
            seenFields.add(field.id);
          }
          if (field.name && !seenFields.has(field.name)) {
            fileFields.push(field.name);
            seenFields.add(field.name);
          }
        }
      });
      if (section.sections) traverse(section.sections);
    });
  };

  if (form.sections) traverse(form.sections);

  if (form.questions && Array.isArray(form.questions)) {
    form.questions.forEach(question => {
      if (isFileType(question.type)) {
        if (question.id && !seenFields.has(question.id)) {
          fileFields.push(question.id);
          seenFields.add(question.id);
        }
        if (question.name && !seenFields.has(question.name)) {
          fileFields.push(question.name);
          seenFields.add(question.name);
        }
      }
    });
  }

  return fileFields;
};

export const separateFormAndFileData = (formData, fileFields) => {
  const cleanFormData = { ...formData };
  const filesData = {};

  const isFileOrBlob = item => item instanceof File || item instanceof Blob;
  const fileFieldsSet = new Set(fileFields);

  Object.keys(formData).forEach(key => {
    // Check if key is a direct match or ends with a file field name/path
    // This handles path-based keys like "section_0_field_name"
    const isFileField = fileFieldsSet.has(key) || fileFields.some(ff => key.endsWith(`_${ff}`));

    if (isFileField) {
      const fieldValue = formData[key];
      if (fieldValue) {
        if (isFileOrBlob(fieldValue)) {
          filesData[key] = [fieldValue];
          delete cleanFormData[key];
        } else if (Array.isArray(fieldValue)) {
          const files = fieldValue.filter(item => isFileOrBlob(item));
          if (files.length > 0) {
            filesData[key] = files;
          }
          delete cleanFormData[key];
        }
      }
    }
  });

  return { formData: cleanFormData, filesData };
};

export const hasFileData = (formData, fileFields) => {
  return fileFields.some(fieldName => {
    const fieldValue = formData[fieldName];
    if (fieldValue instanceof File) return true;
    if (Array.isArray(fieldValue)) {
      return fieldValue.some(item => item instanceof File);
    }
    return false;
  });
};

export const validateFileFields = (formData, fileFields, options = {}) => {
  const { maxSize = 10 * 1024 * 1024, allowedTypes = [] } = options;
  const errors = [];
  fileFields.forEach(fieldName => {
    const fieldValue = formData[fieldName];
    if (fieldValue) {
      const files = Array.isArray(fieldValue) ? fieldValue : [fieldValue];
      files.forEach((file, index) => {
        if (file instanceof File) {
          if (file.size > maxSize) {
            errors.push(`${fieldName}[${index}]: File size exceeds ${Math.round(maxSize / 1024 / 1024)}MB limit`);
          }
          if (allowedTypes.length > 0 && !allowedTypes.some(type => file.type.startsWith(type))) {
            errors.push(`${fieldName}[${index}]: File type ${file.type} not allowed`);
          }
        }
      });
    }
  });
  return { isValid: errors.length === 0, errors };
};

export const getFileFieldConfig = (form, fieldName) => {
  let fieldConfig = null;
  const isFileType = type => ['FILE', 'IMAGE', 'MULTI-FILE', 'AUDIO', 'VIDEO'].includes(type?.toUpperCase());

  if (form.sections && Array.isArray(form.sections)) {
    for (const section of form.sections) {
      if (section.fields && Array.isArray(section.fields)) {
        fieldConfig = section.fields.find(field => (field.name === fieldName || field.id === fieldName) && isFileType(field.type));
        if (fieldConfig) break;
      }
    }
  }
  if (!fieldConfig && form.questions && Array.isArray(form.questions)) {
    fieldConfig = form.questions.find(question => (question.name === fieldName || question.id === fieldName) && isFileType(question.type));
  }
  if (!fieldConfig) {
    return {
      type: 'FILE',
      maxSize: 10 * 1024 * 1024,
      allowedTypes: [],
      multiple: true
    };
  }
  const type = fieldConfig.type?.toUpperCase();
  const isImage = type === 'IMAGE';
  const isMultiFile = type === 'MULTI-FILE';
  const isAudio = type === 'AUDIO';
  const isVideo = type === 'VIDEO';

  return {
    type: fieldConfig.type,
    maxSize: isImage ? 9.5 * 1024 * 1024 : 10 * 1024 * 1024,
    allowedTypes: isImage ? ['image/'] : isAudio ? ['audio/'] : isVideo ? ['video/'] : [],
    multiple: isMultiFile || fieldConfig.multiple === true,
    required: fieldConfig.is_required || fieldConfig.required || false
  };
};

export const getFileFieldTypes = (form, fileFields) => {
  const fieldTypes = {};
  fileFields.forEach(fieldId => {
    const config = getFileFieldConfig(form, fieldId);
    fieldTypes[fieldId] = config.type;
  });
  return fieldTypes;
};

export const getFileFieldIdMap = (form, activeRepeats = {}) => {
  const idMap = {};
  if (!form) return idMap;

  const isFileType = type => ['FILE', 'IMAGE', 'MULTI-FILE', 'AUDIO', 'VIDEO'].includes(type?.toUpperCase());

  // Helper to map a single field
  const mapField = (field, path = '') => {
    if (!field || !isFileType(field.type)) return;
    const name = path ? path + field.name : field.name;
    if (field.id) {
      if (name) idMap[name] = field.id;
      idMap[field.id] = field.id;
    }
  };

  // 1. Map top-level questions
  form.questions?.forEach(q => mapField(q));

  // 2. Recursive traversal of sections and fields
  const traverse = (secs, pathPrefix = '') => {
    secs?.forEach(section => {
      const isRepeat = !!section.is_repeat;

      if (isRepeat) {
        const key = pathPrefix ? `${pathPrefix}${section.name}` : section.name;
        const repeatCount = parseInt(activeRepeats?.[key]) || 0;

        for (let i = 0; i < repeatCount; i++) {
          const itemPath = `${pathPrefix}${section.name}_${i}_`;
          section.fields?.forEach(field => mapField(field, itemPath));
          if (section.sections) traverse(section.sections, itemPath);
        }
      } else {
        section.fields?.forEach(field => mapField(field, pathPrefix));
        if (section.sections) traverse(section.sections, pathPrefix);
      }
    });
  };

  if (form.sections) traverse(form.sections);

  return idMap;
};
